import {
  AD_DESCRIPTION,
  AD_HEADLINE,
  AD_PREVIEW_CAROUSEL_INDEX_KEY,
  AD_TEXT,
  CALL_TO_ACTION,
  CAROUSEL_ITEMS,
  CLICK_THROUGH_LINK,
  CUSTOMIZED_STORY_FILE,
  DESCRIPTION_BLANK,
  DESCRIPTION_ENTER,
  DESCRIPTION_OPTION,
  DESCRIPTIONS_LIST,
  FB_PAGE_CHOICE,
  FB_SPONSOR_PAGE_ID,
  FORMAT_CAROUSEL,
  FORMAT_EXISTING_POST,
  FORMAT_OPTION,
  FORMAT_SINGLE,
  HEADLINE_BLANK,
  HEADLINE_ENTER,
  HEADLINE_OPTION,
  HEADLINES_LIST,
  initialFileValues,
  INSTA_ACCOUNT_CHOICE,
  INSTAGRAM_ACTOR_ID,
  INSTAGRAM_USER_ID,
  IS_CURRENT_MEDIA_CUSTOMIZED_WITH_STORY,
  LEAD_GEN_FORM,
  NAME,
  PRIMARY_TEXT_LIST,
  STORIES_ITEMS,
  TEXT_BLANK,
  TEXT_ENTER,
  TEXT_OPTION
} from '../fields'

import { AD_FORMAT } from '../../../../constants/ads'
import {
  IMAGE_HASH,
  FILE_ID,
  FILE_NAME,
  FILE_URL,
  IFRAME_SRC,
  MEDIA_TYPE,
  VIDEO_PICTURE,
  MEDIA_FILE_OPTION,
  MEDIA_FILE_UPLOAD
} from '../../../ReusableFormFields/AdFileUpload/fields'

export const isEditFormDisabled = ad => {
  if (isCustomAd(ad) && !isCustomAdWithStory(ad)) {
    // Currently we support editing ad customised with story, but there are types of customised ad that we don't support
    return true
  } else if (isExistingPostTypeAd(ad)) {
    // Currently we don't support editing existing post ad
    return true
  }
  return false
}

export const getAdFormat = values => {
  if (!!values && !!values[CAROUSEL_ITEMS]) {
    return values[IS_CURRENT_MEDIA_CUSTOMIZED_WITH_STORY] ? AD_FORMAT.carouselCustom : AD_FORMAT.carousel
  } else {
    return values?.[IS_CURRENT_MEDIA_CUSTOMIZED_WITH_STORY] ? AD_FORMAT.singleCustom : AD_FORMAT.single
  }
}

// ! Workaround to identify is ad "existing post ad"

const getSingleImageCta = ad => ad.creative.object_story_spec?.link_data?.call_to_action?.type?.toUpperCase()
const getSingleVideoCta = ad => ad.creative.object_story_spec?.video_data?.call_to_action?.type?.toUpperCase()
const getExistingPostCta = ad => ad.creative.call_to_action_type

export const isExistingPostTypeAd = ad => {
  return !getSingleImageCta(ad) && !getSingleVideoCta(ad) && !!getExistingPostCta(ad)
}

export const isAllImageUrlsWasRequested = ({ hashes, requestedHashes }) => {
  return hashes?.every(item => {
    return requestedHashes?.includes(item)
  })
}

export const isAllVideoUrlsWasRequested = ({ ids, requestedIds }) => {
  return ids?.every(item => {
    return requestedIds?.includes(item)
  })
}

export const getMissingVideoIds = ({ ids, requestedIds }) => {
  return ids.filter(item => !requestedIds.includes(item))
}

export const getMissingImageHashes = ({ hashes, requestedHashes }) => {
  return hashes.filter(item => !requestedHashes.includes(item))
}

export const isCarousel = ad => !!ad.creative?.object_story_spec?.link_data?.child_attachments
export const isCustomCarousel = ad => false
const isCustomAd = ad => !!ad.creative?.asset_feed_spec
export const isCustomAdWithStory = ad =>
  !!ad.creative.asset_feed_spec?.asset_customization_rules?.find(item =>
    item.customization_spec?.facebook_positions?.includes('story')
  )

const getSingleVideoId = ad => {
  const videoId =
    // general single video ad
    ad?.creative?.object_story_spec?.video_data?.video_id ||
    // existing post
    ad?.creative?.video_id

  return videoId
}

export const getFeedVideoInCustomizedAd = ad => {
  return ad.creative?.asset_feed_spec?.videos?.find(item => {
    return item.adlabels.find(label => {
      return label.name === 'label_other_video'
    })
  })
}

const getStoryVideoInCustomizedAd = ad => {
  return ad.creative.asset_feed_spec?.videos?.find(item => {
    return item.adlabels.find(label => {
      return label.name === 'label_story_video'
    })
  })
}

const isSingleVideoWithCustomStory = ad => !!ad.creative.asset_feed_spec?.videos
const isSingleImage = ad => !!ad.creative?.image_hash || !!ad.creative?.object_story_spec?.link_data?.image_hash

// On campaign summary page we don't have lineItem loaded, but have lineItems list loaded,
// to avoid unnecessary request we can get placement_positions from lineItems list
export const getPlacementPositions = ({ ad, lineItem, lineItems, lineItemWasLoaded, lineItemsWasLoaded }) => {
  if (lineItemWasLoaded) {
    return lineItem && lineItem['placement_positions']
  }

  if (lineItemsWasLoaded) {
    const selectedLineItem = lineItems.find(item => item.id === ad.adset_id)
    return selectedLineItem && selectedLineItem['placement_positions']
  }

  return []
}

export const getImageHashes = ad => {
  const hashes = []

  // carousel
  if (isCarousel(ad)) {
    const carouselHashes = getCarouselImageHashes(ad) || []
    hashes.push(...carouselHashes)
  }

  // single image
  if (isSingleImage(ad)) {
    const singleImageHash = ad.creative.image_hash || ad.creative?.object_story_spec?.link_data?.image_hash
    hashes.push(singleImageHash)
  }

  // customized ad
  if (isCustomAd(ad)) {
    const customizedAdImageHashes = getCustomizedAdImageHashes(ad)
    hashes.push(...customizedAdImageHashes)
  }

  // single image with custom story
  if (isCustomAdWithStory(ad) && !getFeedVideoInCustomizedAd(ad)) {
    const singleImageWithCustomStoryHash = getFeedImageHashInCustomizedAd(ad)
    hashes.push(singleImageWithCustomStoryHash)
  }

  return hashes.filter(hash => !!hash)
}

export const getVideoIds = ad => {
  const videoIds = []

  // single video
  if (!!getSingleVideoId(ad)) {
    const singleVideoId = getSingleVideoId(ad)
    videoIds.push(singleVideoId)
  }

  // video feed and video story ids
  if (isSingleVideoWithCustomStory(ad)) {
    const ids = ad.creative?.asset_feed_spec.videos.map(item => item.video_id)
    videoIds.push(...ids)
  }

  // carousel
  if (isCarousel(ad)) {
    const carouselVideoIds = ad.creative.object_story_spec.link_data.child_attachments
      .filter(item => item.video_id)
      .map(item => item.video_id)
    videoIds.push(...carouselVideoIds)
  }

  return videoIds.filter(id => !!id)
}

export const getVideoImageHashes = ad => {
  const imageHashes = []
  // single video
  if (!!getSingleVideoId(ad)) {
    const imageHash = ad?.creative?.object_story_spec?.video_data?.image_hash
    imageHashes.push(imageHash)
  }

  // video feed and video story hashes
  if (isSingleVideoWithCustomStory(ad)) {
    const videoImageHashes = ad.creative.asset_feed_spec.videos.map(item => item.thumbnail_hash)
    imageHashes.push(...videoImageHashes)
  }

  // carousel
  if (isCarousel(ad)) {
    const carouselVideoImageHashes = ad.creative.object_story_spec.link_data.child_attachments
      .filter(item => item.video_id)
      .map(item => item.image_hash)
    imageHashes.push(...carouselVideoImageHashes)
  }

  return imageHashes.filter(hash => !!hash)
}

const getFeedImageHashInCustomizedAd = ad => {
  // Single image with custom story has 3 images with different ratio (width/height)
  // we have to find the correct one, (with ratio 1:1)
  // Correct asset_customization_rule has facebook_position that includes "feed",
  // or doesn't have facebook_positions at all AND doesn't have instagram_positions

  const feed = ad.creative.asset_feed_spec.asset_customization_rules?.find(
    item =>
      item.customization_spec?.facebook_positions?.includes('feed') ||
      (!item.customization_spec.facebook_positions && !item.customization_spec.instagram_positions)
  )
  const feedImageLabelName = feed?.image_label?.name

  return ad.creative.asset_feed_spec.images?.find(item => item?.adlabels?.[0].name === feedImageLabelName)?.hash
}

const getStoryImageHashInCustomizedAd = ad => {
  const story = ad.creative.asset_feed_spec.asset_customization_rules?.find(item =>
    item.customization_spec?.facebook_positions?.includes('story')
  )
  const storyImageLabelName = story?.image_label?.name

  return ad.creative.asset_feed_spec.images?.find(item => item?.adlabels?.[0].name === storyImageLabelName)?.hash
}

const getCarouselImageHashes = ad => {
  return ad.creative.object_story_spec.link_data.child_attachments
    .filter(item => !item.video_id)
    .map(item => item.image_hash)
}

const getCustomizedAdImageHashes = ad => {
  return ad.creative.asset_feed_spec?.images?.map(item => item.hash) || []
}

export const getSingleImageOrVideo = ({ ad, imageUrls, videoUrls }) => {
  if (isCustomAd(ad)) {
    if (isCustomAdWithStory(ad)) {
      return getSingleCustomizedAdWithStory({ ad, imageUrls, videoUrls })
    } else {
      return getSingleCustomizedAd({ ad, imageUrls, videoUrls })
    }
  } else {
    return getSingleItem({ ad, imageUrls, videoUrls })
  }
}

const getCustomizedAdReusablePayload = ({ ad }) => {
  const primaryTextList = ad.creative.asset_feed_spec?.bodies
  const descriptionsList = ad.creative.asset_feed_spec?.descriptions
  const headlinesList = ad.creative.asset_feed_spec?.titles

  const adText = ad.creative.asset_feed_spec?.bodies?.[0]?.text
  const description = ad.creative.asset_feed_spec?.descriptions?.[0]?.text
  const headline = ad.creative.asset_feed_spec?.titles?.[0]?.text

  const link =
    ad.creative.object_story_spec?.link_data?.link || ad.creative?.asset_feed_spec?.link_urls?.[0]?.website_url

  const callToAction =
    ad.creative.object_story_spec?.link_data?.call_to_action?.type ||
    ad.creative.asset_feed_spec?.call_to_action_types?.[0]?.toUpperCase()

  const sponsorFacebookPageId = ad.creative?.facebook_branded_content?.[FB_SPONSOR_PAGE_ID]

  const singleMediaItem = {
    // description
    [AD_DESCRIPTION]: description,
    [DESCRIPTIONS_LIST]: descriptionsList ? descriptionsList : [{ text: '' }],
    [DESCRIPTION_OPTION]: !!description ? DESCRIPTION_ENTER : DESCRIPTION_BLANK,
    // headline
    [AD_HEADLINE]: headline,
    [HEADLINES_LIST]: headlinesList ? headlinesList : [{ text: '' }],
    [HEADLINE_OPTION]: !!headline ? HEADLINE_ENTER : HEADLINE_BLANK,
    // link
    [CLICK_THROUGH_LINK]: link,
    [CALL_TO_ACTION]: callToAction,
    // sponsor page
    [FB_SPONSOR_PAGE_ID]: sponsorFacebookPageId,
    [LEAD_GEN_FORM]: ad.creative?.asset_feed_spec?.call_to_actions?.[0]?.value?.[LEAD_GEN_FORM]
  }

  return {
    // name
    [NAME]: ad.name,
    // primary text
    [AD_TEXT]: adText,
    [PRIMARY_TEXT_LIST]: primaryTextList ? primaryTextList : [{ text: '' }],
    [TEXT_OPTION]: !!adText ? TEXT_ENTER : TEXT_BLANK,
    // social accounts
    [FB_PAGE_CHOICE]: ad.creative.actor_id,
    [INSTA_ACCOUNT_CHOICE]: getInstagramAccountId(ad),

    singleMediaItem
  }
}

const isVideoIframe = (video, videoUrls) => {
  const videoData = videoUrls.find(videoUrl => videoUrl.id === video?.video_id)

  return !!videoData?.iframe_src
}

const getInstagramAccountId = ad => {
  // For some reason instagram account id may appear in one of 2 fields (instagram_actor_id or instagram_user_id)
  return ad.creative[INSTAGRAM_ACTOR_ID] || ad.creative[INSTAGRAM_USER_ID]
}

export const getSingleCustomizedAd = ({ ad, imageUrls, videoUrls }) => {
  const reusableCustomizedPayload = getCustomizedAdReusablePayload({ ad, imageUrls, videoUrls })

  const imageHash =
    ad.creative.object_story_spec?.link_data?.image_hash || ad.creative.asset_feed_spec?.images?.[0]?.hash

  const imageFileUrl = getItemFileInfo({ item: { image_hash: imageHash }, imageUrls })?.url
  const imageFileName = getItemFileInfo({ item: { image_hash: imageHash }, imageUrls })?.name

  // video
  const video = ad.creative.asset_feed_spec?.videos?.[0]
  const videoFileUrl = getItemFileInfo({ item: video, videoUrls })?.url
  const videoFileName = getItemFileInfo({ item: video, videoUrls })?.name

  // Try to find square thumbnail, if not take just first
  const videoPicture =
    ad.creative.asset_feed_spec?.videos?.find(item => item.thumbnail_url?.includes('160x160'))?.thumbnail_url ||
    ad.creative.asset_feed_spec?.videos?.find(item => item.adlabels?.name === 'label_other_video')?.thumbnail_url ||
    ad.creative.asset_feed_spec?.videos?.[0]?.thumbnail_url

  const singleVideo = {
    [IFRAME_SRC]: isVideoIframe(video, videoUrls),
    [FILE_URL]: videoFileUrl,
    [VIDEO_PICTURE]: videoPicture,
    [FILE_ID]: video?.video_id,
    [FILE_NAME]: videoFileName,
    [MEDIA_TYPE]: 'video'
  }

  const singleImage = {
    [IMAGE_HASH]: imageHash,
    [FILE_URL]: imageFileUrl,
    [FILE_NAME]: imageFileName,
    [MEDIA_TYPE]: 'image'
  }

  // Customised ads can have different payload.
  // Some of them have only image, some of them have only video, some of them have both.
  // It may be video for which we can get video_url, it may be video for which we can get only videoPicture.
  // So we try to represent it in next order: videoUrl -> videoPicture -> imageUrl
  const isVideo = !!videoFileUrl || !!videoPicture

  const singleMediaItem = {
    ...reusableCustomizedPayload.singleMediaItem,
    ...(!!isVideo ? singleVideo : singleImage)
  }

  return {
    ...reusableCustomizedPayload,
    ...singleMediaItem,
    singleMediaItem
  }
}

export const getSingleCustomizedAdWithStory = ({ ad, imageUrls, videoUrls }) => {
  const reusableCustomizedPayload = getCustomizedAdReusablePayload({ ad, imageUrls, videoUrls })

  const imageHash = getFeedImageHashInCustomizedAd(ad)
  const feedVideo = getFeedVideoInCustomizedAd(ad)

  const singleVideo = {
    [IFRAME_SRC]: isVideoIframe(feedVideo, videoUrls),
    [FILE_URL]: getItemFileInfo({ item: feedVideo, videoUrls })?.url,
    [VIDEO_PICTURE]: feedVideo?.thumbnail_url,
    [FILE_ID]: feedVideo?.video_id,
    [IMAGE_HASH]: feedVideo?.thumbnail_hash,
    [FILE_NAME]: getItemFileInfo({ item: feedVideo, videoUrls })?.name,
    [MEDIA_TYPE]: 'video'
  }

  const singleImage = {
    [IMAGE_HASH]: imageHash,
    [FILE_URL]: getItemFileInfo({ item: { image_hash: imageHash }, imageUrls })?.url,
    [FILE_NAME]: getItemFileInfo({ item: { image_hash: imageHash }, imageUrls })?.name,
    [MEDIA_TYPE]: 'image'
  }

  const storyImageHash = getStoryImageHashInCustomizedAd(ad)
  const storyImage = {
    [IMAGE_HASH]: storyImageHash,
    [FILE_URL]: getItemFileInfo({ item: { image_hash: storyImageHash }, imageUrls })?.url,
    [FILE_NAME]: getItemFileInfo({ item: { image_hash: storyImageHash }, imageUrls })?.name,
    [MEDIA_TYPE]: 'image'
  }

  const storyVideoId = getStoryVideoInCustomizedAd(ad)?.video_id
  const storyVideoThumbnailHash = getStoryVideoInCustomizedAd(ad)?.thumbnail_hash
  const storyVideo = {
    [IMAGE_HASH]: storyVideoThumbnailHash,
    [FILE_NAME]: getVideoInfo({ videoUrls, videoId: storyVideoId })?.name,
    [FILE_URL]: getVideoInfo({ videoUrls, videoId: storyVideoId })?.url,
    [VIDEO_PICTURE]: getStoryVideoInCustomizedAd(ad)?.thumbnail_url,
    [FILE_ID]: storyVideoId,
    [MEDIA_TYPE]: 'video'
  }

  const story = storyVideoId ? storyVideo : storyImage

  const singleMediaItem = {
    ...reusableCustomizedPayload.singleMediaItem,
    ...(!!feedVideo ? singleVideo : singleImage)
  }

  return {
    [IS_CURRENT_MEDIA_CUSTOMIZED_WITH_STORY]: true,
    [CUSTOMIZED_STORY_FILE]: {
      ...initialFileValues,
      ...story,
      [MEDIA_FILE_OPTION]: MEDIA_FILE_UPLOAD
    },

    ...reusableCustomizedPayload,
    ...singleMediaItem,
    singleMediaItem
  }
}

const getSingleImage = ({ ad, imageUrls }) => {
  const singleImageAdText = ad.creative.object_story_spec?.link_data?.message || ad.creative.body
  const singleImageHeadline = ad.creative.object_story_spec?.link_data?.name
  const singleImageDescription = ad.creative.object_story_spec?.link_data?.description

  const imageHash = ad.creative.image_hash || ad.creative?.object_story_spec?.link_data?.image_hash

  // social accounts
  const facebookPageId = ad.creative.actor_id || ad.creative?.object_story_spec?.page_id
  const facebookPageLinkedInstagramAccountId = getInstagramAccountId(ad)
  const sponsorFacebookPageId = ad.creative?.facebook_branded_content?.[FB_SPONSOR_PAGE_ID]

  const cta = getSingleImageCta(ad) || getExistingPostCta(ad)

  const singleMediaItem = {
    // description
    [AD_DESCRIPTION]: singleImageDescription,
    [DESCRIPTION_OPTION]: singleImageDescription ? DESCRIPTION_ENTER : DESCRIPTION_BLANK,
    [DESCRIPTIONS_LIST]: singleImageDescription ? [{ text: singleImageDescription }] : [{ text: '' }],
    // headline
    [AD_HEADLINE]: singleImageHeadline,
    [HEADLINE_OPTION]: !!singleImageHeadline ? HEADLINE_ENTER : HEADLINE_BLANK,
    [HEADLINES_LIST]: singleImageHeadline ? [{ text: singleImageHeadline }] : [{ text: '' }],
    // link
    [CLICK_THROUGH_LINK]:
      ad.creative.object_story_spec?.link_data?.call_to_action?.value?.link ||
      ad.creative.object_story_spec?.link_data?.link,
    [CALL_TO_ACTION]: cta,
    // file
    [MEDIA_TYPE]: 'image',
    // [FILE_URL]: imageUrls[0]?.url,
    [FILE_URL]: getImageInfo({ imageUrls, imageHash })?.url,
    [FILE_NAME]: getImageInfo({ imageUrls, imageHash })?.name,
    [IMAGE_HASH]: imageHash,
    [LEAD_GEN_FORM]: ad.creative?.object_story_spec?.link_data?.call_to_action?.value?.[LEAD_GEN_FORM]
  }

  return {
    [NAME]: ad.name,
    // primary text
    [AD_TEXT]: singleImageAdText,
    [PRIMARY_TEXT_LIST]: singleImageAdText ? [{ text: singleImageAdText }] : [{ text: '' }],
    [TEXT_OPTION]: !!singleImageAdText ? TEXT_ENTER : TEXT_BLANK,
    // social accounts
    [FB_PAGE_CHOICE]: facebookPageId,
    [INSTA_ACCOUNT_CHOICE]: facebookPageLinkedInstagramAccountId,
    [FB_SPONSOR_PAGE_ID]: sponsorFacebookPageId,

    // media
    // todo: Refactor create ad form and adCard to use singleMediaItem nested object
    ...singleMediaItem,
    singleMediaItem
  }
}

const getSingleVideo = ({ ad, videoUrls }) => {
  const singleVideoAdText =
    // general single video ad
    ad.creative.object_story_spec?.video_data?.message ||
    // existing post
    ad.creative.body

  const singleVideoHeadline = ad.creative.object_story_spec?.video_data?.title
  const singleVideoDescription = ad.creative.object_story_spec?.video_data?.link_description

  const videoId = getSingleVideoId(ad)

  const cta = getSingleVideoCta(ad) || getExistingPostCta(ad)

  // social accounts
  const facebookPageId = ad.creative.actor_id || ad.creative?.object_story_spec?.page_id
  const facebookPageLinkedInstagramAccountId = getInstagramAccountId(ad)
  const sponsorFacebookPageId = ad.creative?.facebook_branded_content?.[FB_SPONSOR_PAGE_ID]

  const singleMediaItem = {
    // description
    [AD_DESCRIPTION]: ad.creative.object_story_spec?.video_data?.link_description,
    [DESCRIPTION_OPTION]: !!singleVideoDescription ? DESCRIPTION_ENTER : DESCRIPTION_BLANK,
    [DESCRIPTIONS_LIST]: singleVideoDescription ? [{ text: singleVideoDescription }] : [{ text: '' }],
    // headline
    [AD_HEADLINE]: singleVideoHeadline,
    [HEADLINE_OPTION]: !!singleVideoHeadline ? HEADLINE_ENTER : HEADLINE_BLANK,
    [HEADLINES_LIST]: singleVideoHeadline ? [{ text: singleVideoHeadline }] : [{ text: '' }],
    // link
    [CLICK_THROUGH_LINK]:
      ad.creative.object_story_spec?.video_data?.call_to_action?.value?.link ||
      ad.creative.object_story_spec?.video_data?.link,
    [CALL_TO_ACTION]: cta,
    // file
    [MEDIA_TYPE]: 'video',
    [FILE_ID]: videoId,
    [VIDEO_PICTURE]: ad.creative.object_story_spec?.video_data?.image_url,
    [IMAGE_HASH]: ad.creative.object_story_spec?.video_data?.image_hash,
    [FILE_URL]: getVideoInfo({ videoUrls, videoId })?.url,
    [FILE_NAME]: getVideoInfo({ videoUrls, videoId })?.name,
    [LEAD_GEN_FORM]: ad.creative?.object_story_spec?.video_data?.call_to_action?.value?.[LEAD_GEN_FORM]
  }

  return {
    [NAME]: ad.name,
    // primary text
    [AD_TEXT]: singleVideoAdText,
    [TEXT_OPTION]: !!singleVideoAdText ? TEXT_ENTER : TEXT_BLANK,
    [PRIMARY_TEXT_LIST]: singleVideoAdText ? [{ text: singleVideoAdText }] : [{ text: '' }],

    // social accounts
    [FB_PAGE_CHOICE]: facebookPageId,
    [INSTA_ACCOUNT_CHOICE]: facebookPageLinkedInstagramAccountId,
    [FB_SPONSOR_PAGE_ID]: sponsorFacebookPageId,

    // media
    // todo: Refactor create ad form and adCard to use singleMediaItem nested object
    ...singleMediaItem,
    singleMediaItem
  }
}

export const getSingleItem = ({ ad, imageUrls, videoUrls }) => {
  return !!getSingleVideoId(ad) ? getSingleVideo({ ad, videoUrls }) : getSingleImage({ ad, imageUrls })
}

export const getItemFileInfo = ({ item, imageUrls, videoUrls }) => {
  if (item?.video_id) {
    return getVideoInfo({ videoUrls, videoId: item?.video_id })
  } else {
    return getImageInfo({ imageUrls, imageHash: item?.image_hash })
  }
}

const getVideoInfo = ({ videoUrls, videoId }) => {
  return videoUrls.find(video => video.id === videoId)
}

const getImageInfo = ({ imageUrls, imageHash }) => {
  return imageUrls?.find(item => imageHash === item.hash)
}

export const getCarouselItems = ({ ad, imageUrls, videoUrls }) => {
  return ad.creative.object_story_spec.link_data.child_attachments.map(item => {
    const headline = item.name
    const description = item.description

    const mediaType = item.video_id ? 'video' : 'image'

    const mediaFile = getItemFileInfo({ item, imageUrls, videoUrls })
    const mediaFileUrl = mediaFile?.url
    const mediaFileName = mediaFile?.name

    const isMediaVideoIframe = mediaType === 'video' && isVideoIframe(item, videoUrls)

    return {
      // description
      [DESCRIPTION_OPTION]: !!description ? DESCRIPTION_ENTER : DESCRIPTION_BLANK,
      [AD_DESCRIPTION]: description,
      [DESCRIPTIONS_LIST]: !!description ? [{ text: description }] : [{ text: '' }],
      // headline
      [HEADLINE_OPTION]: !!headline ? HEADLINE_ENTER : HEADLINE_BLANK,
      [AD_HEADLINE]: headline,
      [HEADLINES_LIST]: !!headline ? [{ text: headline }] : [{ text: '' }],
      // link
      [CALL_TO_ACTION]: item.call_to_action?.type?.toUpperCase(),
      [CLICK_THROUGH_LINK]: item.call_to_action?.type?.value || item.link,
      // file
      [MEDIA_TYPE]: mediaType,
      // todo check after editFinalize START NEW FIELDS
      [IMAGE_HASH]: item.image_hash,
      [FILE_ID]: item.video_id,
      [VIDEO_PICTURE]: item.image_url,
      // FINISH NEW FIELDS
      [FILE_URL]: mediaFileUrl,
      [FILE_NAME]: mediaFileName,
      [IFRAME_SRC]: isMediaVideoIframe
    }
  })
}

export const getStoriesItems = ({ ad, imageUrls, videoUrls }) => {
  return isCarousel(ad)
    ? getCarouselItems({ ad, imageUrls, videoUrls })
    : [getSingleImageOrVideo({ ad, imageUrls, videoUrls })]
}

const getInitialCarouselIndexIdentified = ({ adId, isEditForm }) => {
  // for AdCarsListItem previews we use ad ids, for edit/create form separate string keys
  // it helps to avoid switching carousel both in edit/ad list
  if (isEditForm) {
    return 'adEditForm'
  } else if (adId) {
    return adId
  } else {
    return ''
  }
}

const isExistingPost = ad => {
  return !!ad?.creative?.object_story_id
}

const getInitialAdFormatOption = ad => {
  if (isCarousel(ad)) {
    return FORMAT_CAROUSEL
  } else if (isExistingPost(ad)) {
    return FORMAT_EXISTING_POST
  } else {
    return FORMAT_SINGLE
  }
}

export const transformValuesToFE = ({ ad, adWasLoaded, placementPositions, imageUrls, videoUrls, isEditForm }) => {
  if (!adWasLoaded) return

  const { id: adId } = ad

  const isCarouselAd = isCarousel(ad)

  return {
    [FORMAT_OPTION]: getInitialAdFormatOption(ad),

    [AD_PREVIEW_CAROUSEL_INDEX_KEY]: getInitialCarouselIndexIdentified({ adId, isEditForm }),

    // Single item
    ...getSingleImageOrVideo({ ad, imageUrls, videoUrls }),

    // Carousel items
    ...(isCarouselAd && {
      [CAROUSEL_ITEMS]: getCarouselItems({ ad, imageUrls, videoUrls })
    }),

    // Story items
    [STORIES_ITEMS]: getStoriesItems({ ad, imageUrls, videoUrls })
  }
}
